import { ChangeDetectionStrategy, Component, Input, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DxPopupModule } from 'devextreme-angular';
import type { PositionConfig } from 'devextreme/animation/position';

/**
 * The floating settings panel used alongside a rendered report.
 *
 * Extracted from production-report-view, which established the shape: a small
 * draggable popup anchored to the top-right of the document, collapsible to its
 * title bar, and deliberately NOT modal — no shading and no hide-on-outside-click,
 * because the whole point is to compare a setting against the rendered page while
 * changing it. A sidebar cannot do that without stealing width from the document,
 * and a modal cannot do it at all.
 *
 * Content is projected, so callers keep their own form. Only the container is
 * shared, which is the part that was being reimplemented.
 */
@Component({
  selector: 'ag-shr-report-settings-popup',
  standalone: true,
  imports: [CommonModule, DxPopupModule],
  templateUrl: './report-settings-popup.component.html',
  styleUrls: ['./report-settings-popup.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReportSettingsPopupComponent {
  @Input() title = 'Report Settings';
  @Input() visible = true;
  @Input() width: number | string = 360;
  @Input() minWidth: number | string = 320;

  /**
   * CSS selector of the element to anchor against — normally the preview shell, so
   * the panel tracks the document rather than the viewport.
   */
  @Input() anchorSelector = '';

  protected readonly isCollapsedSig = signal(false);

  protected get position(): PositionConfig {
    return {
      my: { x: 'right', y: 'top' },
      at: { x: 'right', y: 'top' },
      of: this.anchorSelector || window,
      offset: { x: -16, y: 72 },
    };
  }

  protected get wrapperAttr(): Record<string, string> {
    return {
      class:
        'ag-shr-report-settings-popup no-print' +
        (this.isCollapsedSig() ? ' ag-shr-report-settings-popup--collapsed' : ''),
    };
  }

  protected readonly toggleCollapse = (): void => {
    this.isCollapsedSig.update(isCollapsed => !isCollapsed);
  };
}
